import { assertUnreachable, isEmpty } from '~libs.typescript';
import { useMemo } from '~libs.ui.react';

import {
   AnnulledPrescription,
   AnnulledPrescriptionPackage,
   PrescriptionPackage,
   RecipientCategory,
   SentPrescriptionPackage,
   UnsentPrescription,
   ValidatedPrescription,
   ValidatedPrescriptionPackage,
} from '../types';
import { isPrescriptionAnnulled } from '../guards/isPrescriptionAnnulled';
import { isValidatedPrescriptionPackage } from '../guards/isValidatedPrescriptionPackage';
import { isPrescriptionValidated } from '../guards/isPrescriptionValidated';
import { isUnsentPrescriptionPackage } from '../guards/isUnsentPrescriptionPackage';

type PrescriptionPackagePrescriptionsExtractionResult = {
   annulledPrescriptions: AnnulledPrescription[];
   validatedPrescriptions: ValidatedPrescription[];
};

const extractPrescriptionPackagePrescriptions = (
   prescriptionPackage: ValidatedPrescriptionPackage
): PrescriptionPackagePrescriptionsExtractionResult => {
   return prescriptionPackage.prescriptions.reduce<PrescriptionPackagePrescriptionsExtractionResult>(
      (result, prescription) => {
         if (isPrescriptionAnnulled(prescription)) {
            result.annulledPrescriptions.push(prescription);
            return result;
         }

         if (isPrescriptionValidated(prescription)) {
            result.validatedPrescriptions.push(prescription);
            return result;
         }

         return assertUnreachable();
      },
      {
         validatedPrescriptions: [],
         annulledPrescriptions: [],
      } as PrescriptionPackagePrescriptionsExtractionResult
   );
};

type UsePrescriptionPackagesReturnFn = {
   unsentPrescriptions: UnsentPrescription[];
   initialRecipientCategory: RecipientCategory;
   validatedPrescriptionPackages: ValidatedPrescriptionPackage[];
   sentPrescriptionPackages: SentPrescriptionPackage[];
   sentPrescriptionsCount: number;
   validatedPrescriptionsCount: number;
   annulledPrescriptionPackages: AnnulledPrescriptionPackage[];
};

export const usePrescriptionPackages = (
   prescriptionPackages: PrescriptionPackage[]
): UsePrescriptionPackagesReturnFn => {
   return useMemo(() => {
      return prescriptionPackages.reduce<UsePrescriptionPackagesReturnFn>(
         (result, prescriptionPackage: PrescriptionPackage) => {
            if (isValidatedPrescriptionPackage(prescriptionPackage)) {
               result.sentPrescriptionPackages.push(prescriptionPackage);
               result.sentPrescriptionsCount += prescriptionPackage.prescriptions.length;

               const { annulledPrescriptions, validatedPrescriptions } = extractPrescriptionPackagePrescriptions(
                  prescriptionPackage
               );

               if (!isEmpty(annulledPrescriptions)) {
                  result.annulledPrescriptionPackages.push({
                     ...prescriptionPackage,
                     prescriptions: annulledPrescriptions,
                  });
               }

               if (!isEmpty(validatedPrescriptions)) {
                  result.validatedPrescriptionsCount += validatedPrescriptions.length;
                  result.validatedPrescriptionPackages.push({
                     ...prescriptionPackage,
                     prescriptions: validatedPrescriptions,
                  });
               }

               return result;
            }

            if (isUnsentPrescriptionPackage(prescriptionPackage)) {
               const unsentPrescriptions = prescriptionPackage.prescriptions;
               result.initialRecipientCategory = prescriptionPackage.recipientCategory ?? 'STANDARD';
               result.unsentPrescriptions.push(...unsentPrescriptions);
               return result;
            }

            return assertUnreachable();
         },
         {
            unsentPrescriptions: [],
            initialRecipientCategory: 'STANDARD',
            validatedPrescriptionPackages: [],
            sentPrescriptionPackages: [],
            annulledPrescriptionPackages: [],
            sentPrescriptionsCount: 0,
            validatedPrescriptionsCount: 0,
         } as UsePrescriptionPackagesReturnFn
      );
   }, [prescriptionPackages]);
};
